import { useSelector } from "react-redux";
import type { RootState } from "@store/store";
import { PERMISSIONS, type Permission, type UserRole } from "@constants/user.roles";
import { normalizePermissionIdentifier } from "@utils/permissionIdentifiers";
import { useProfile } from "@features/Profile/services/GetProfile";
import { withStaticPrototypePermissions } from "@utils/psdaAdminAccess";

/** Map API role strings to keys used in roleDisplayNames (handles spaces, case, hyphens). */
const normalizeRoleKey = (role: string): string =>
	role.trim().toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_");

function userHasPermissionString(userPermissions: string[], permission: Permission): boolean {
	const want = normalizePermissionIdentifier(String(permission));
	return userPermissions.some(p => normalizePermissionIdentifier(p) === want);
}

/**
 * Custom hook for handling permission-based access control
 * @description Provides utility functions to check user permissions from API response
 */
export const usePermissions = () => {
	const { user } = useSelector((state: RootState) => state.user);
	const { data: profile } = useProfile();

	/**
	 * Get user's roles from the roles array
	 */
	const getUserRoles = (): string[] => {
		return user?.roles || [];
	};

	/**
	 * Get user's permissions from the permissions array
	 */
	const getUserPermissions = (): string[] => {
		const basePermissions = user?.permissions || [];
		return withStaticPrototypePermissions(basePermissions, {
			roles: getUserRoles(),
			organizationTypeId: profile?.organizationTypeId,
			userTypeName: user?.userType?.name,
		});
	};

	/**
	 * Check if user has a specific permission
	 * @param permission - The permission to check
	 * @returns boolean indicating if user has the permission
	 */
	const hasPermission = (permission: Permission): boolean => {
		const userPermissions = getUserPermissions();
		if (userHasPermissionString(userPermissions, permission)) return true;
		if (userHasPermissionString(userPermissions, PERMISSIONS.ALL_READ)) return true;
		return false;
	};

	/**
	 * Check if user has a specific permission explicitly (no all.read bypass).
	 * Use for action permissions (approve, reject, create, etc.) where all.read should not grant access.
	 */
	const hasExplicitPermission = (permission: Permission): boolean => {
		return userHasPermissionString(getUserPermissions(), permission);
	};

	/**
	 * Check if user has any of the specified permissions explicitly (no all.read bypass).
	 */
	const hasAnyExplicitPermission = (permissions: Permission[]): boolean => {
		const userPermissions = getUserPermissions();
		return permissions.some(p => userHasPermissionString(userPermissions, p));
	};

	/**
	 * Check if user has all of the specified permissions explicitly (no all.read bypass).
	 */
	const hasAllExplicitPermissions = (permissions: Permission[]): boolean => {
		const userPermissions = getUserPermissions();
		return permissions.every(p => userHasPermissionString(userPermissions, p));
	};

	/**
	 * Check if user has any of the specified permissions
	 * @param permissions - Array of permissions to check
	 * @returns boolean indicating if user has any of the permissions
	 */
	const hasAnyPermission = (permissions: Permission[]): boolean => {
		const userPermissions = getUserPermissions();
		if (userHasPermissionString(userPermissions, PERMISSIONS.ALL_READ)) return true;
		return permissions.some(p => userHasPermissionString(userPermissions, p));
	};

	/**
	 * Check if user has all of the specified permissions
	 * @param permissions - Array of permissions to check
	 * @returns boolean indicating if user has all of the permissions
	 */
	const hasAllPermissions = (permissions: Permission[]): boolean => {
		const userPermissions = getUserPermissions();
		if (userHasPermissionString(userPermissions, PERMISSIONS.ALL_READ)) return true;
		return permissions.every(p => userHasPermissionString(userPermissions, p));
	};

	/**
	 * Check if user has a specific role
	 * @param role - The role to check
	 * @returns boolean indicating if user has the role
	 */
	const hasRole = (role: UserRole): boolean => {
		const userRoles = getUserRoles();
		return userRoles.includes(role);
	};

	/**
	 * Check if user has any of the specified roles
	 * @param roles - Array of roles to check
	 * @returns boolean indicating if user has any of the roles
	 */
	const hasAnyRole = (roles: UserRole[]): boolean => {
		const userRoles = getUserRoles();
		return roles.some(role => userRoles.includes(role));
	};

	/**
	 * Get user's primary role display name
	 */
	const getUserRoleDisplayName = (): string => {
		const userRoles = getUserRoles();
		if (userRoles.length === 0) return "Unknown Role";

		// Get the first role (primary role)
		const primaryRole = userRoles[0];

		const roleDisplayNames: Record<string, string> = {
			super_admin: "Super Admin",
			system_admin: "System Admin",
			"secretary_sd&ed": "Secretary SD&ED",
			assistant_director: "Assistant Director",
			deputy_director: "Deputy Director",
			director: "Director",
			additional_director: "Additional Director",
			director_general: "Director General",
		};

		const key = normalizeRoleKey(primaryRole);
		return roleDisplayNames[key] || roleDisplayNames[primaryRole] || primaryRole;
	};

	/**
	 * Get all user roles display names
	 */
	const getUserRolesDisplayNames = (): string[] => {
		const userRoles = getUserRoles();

		const roleDisplayNames: Record<string, string> = {
			super_admin: "Super Admin",
			system_admin: "System Admin",
			"secretary_sd&ed": "Secretary SD&ED",
			assistant_director: "Assistant Director",
			deputy_director: "Deputy Director",
			director: "Director",
			additional_director: "Additional Director",
			director_general: "Director General",
		};

		return userRoles.map(role => {
			const key = normalizeRoleKey(role);
			return roleDisplayNames[key] || roleDisplayNames[role] || role;
		});
	};

	return {
		user,
		userRoles: getUserRoles(),
		userPermissions: getUserPermissions(),
		userRoleDisplayName: getUserRoleDisplayName(),
		userRolesDisplayNames: getUserRolesDisplayNames(),
		hasPermission,
		hasExplicitPermission,
		hasAnyExplicitPermission,
		hasAllExplicitPermissions,
		hasAnyPermission,
		hasAllPermissions,
		hasRole,
		hasAnyRole,
		PERMISSIONS, // Export permissions for easy access
	};
};
